import axios from 'axios';
import {trackPromise} from 'react-promise-tracker';
import _ from 'lodash';
import {apiRegistry} from '../util/apiRegistry';
import {useMessageHubEmit} from '@ey/voltron-core';
import {daasErrorCodes, HttpStatusCodes, messageHub, messageType, objectNotFoundActions} from '../util/uiconstants';
import {API_ERRORS} from '../actions/actiontypes';

/*
 * Redux Middleware for handling API calls
 * On successful api response, the response and responseObj are attached to the action and passed to next middleware
 * On Error and error object is attached to the action and API_ERROR action is dispatched
 * Injection of config into the middleware to use it for authentication
 * config can be passed as a parameter to the middleware in applyMiddleware method when creating the store
 *
 */
const apiMiddleware = (config) => (store) => (next) => (action) => {
	const __NAME = 'API Middleware',
		GET_ONLINE_STATE = 'GET_ONLINE_STATE';

	// skip to next middleware for API_ERROR action
	if (action.type === API_ERRORS) {
		next(action);
		return Promise.reject(action.error);
	}

	/* Existence of the meta.api object is the trigger to treat this
	 * action as an API call. */
	if (!(action.meta && action.meta.api)) {
		next(action);
		return Promise.resolve(`[${__NAME}] - action.type:`, action.type, 'Skipping API call since API meta not available');
	}

	let {method, url, data, onSuccess, onFailure, headers, timeout, trackWithArea, responseType} = action.meta.api,
		auth = action.meta.auth;

	// Check for auth error and skip api call incase of auth error;
	if (!(action.meta && action.meta.docHelper)) {
		if (auth.error && !auth.token) {
			next(action);
			return Promise.reject(auth.error);
			//return Promise.resolve('Skipping API call since token is unavailable');
		}
	}

	const promise = new Promise((resolve, reject) => {
		if (!config) {
			const configMsg = `
			Config unavailable.
			The AD package requires config to get the authorization token for the api call.`;
			console.log(`[${__NAME}] - action.type:`, action.type, configMsg);
		}

		/*
		 * If config is not passed as parameter to the middleware
		 * the below function checkLoggedIn from AD package will skip authentication
		 */

		const customHeaders = headers || {};
		const needsJSON = ['post', 'put', 'patch', 'delete'].includes(method.toLowerCase()) && !customHeaders['Content-Type'];
		if (!(action.meta && action.meta.docHelper)) {
			if (auth.token) {
				customHeaders.Authorization = 'Bearer ' + auth.token;
			}
		}
		customHeaders['Content-Type'] =
			method.toLowerCase() === 'patch' ? 'application/json-patch+json' : needsJSON ? 'application/json' : customHeaders['Content-Type'];

		// Register API request with the registry
		apiRegistry.registerRequest(url);

		axios({
			method,
			url,
			data,
			onSuccess,
			onFailure,
			timeout: timeout || 0,
			headers: customHeaders,
			responseType: responseType || ''
		})
			.then((response) => {
				// Complete API request in the registry
				apiRegistry.completeRequest(url);

				action.response = response.data;
				action.responseObj = response;
				next(action);

				// This is required ONLY for returning respose data to Voltron components
				// Do not use this to intercept Api data instead of store use
				let value;
				if (action.resolve) {
					value = response.data;
				}

				if (responseType === 'blob') {
					value = response;
				}

				return resolve(value);
			})
			.catch((error) => {
				// Complete API request in the registry (even in case of error)
				apiRegistry.completeRequest(url);
				const isModalOpen = store.getState().isModalOpen;

				const isObjectNotFoundCase =
					objectNotFoundActions.includes(action?.type) &&
					(action?.action?.error?.response?.status === HttpStatusCodes.NotFound || error?.status === HttpStatusCodes.NotFound);

				// Error incase of IP address change for the user.
				if (
					error &&
					error.response &&
					error.response.headers &&
					error.response.headers['www-authenticate'] &&
					error.response.headers['www-authenticate'].toLowerCase().split('invalid ipaddress claim').length > 1
				) {
					localStorage.clear();
					sessionStorage.clear();
					// TODO: update the below error object creation
					// recreating the error object in order to make it compatible with Errors.js
					error.response.data = {};
					error.response.data.errors = {};
					error.response.data.errors.Error = [JSON.stringify({ErrorCode: 401000})];
					return next({type: 'API_ERRORS', error, action});
				} else if (
					error &&
					error.response &&
					error.response.headers &&
					error.response.data &&
					error.response.data.ErrorCode &&
					error.response.data.ErrorCode.length > 0
				) {
					let documentSettingConfiguration = store.getState().documentSettingConfiguration;
					error = {...error, documentSettingConfiguration: documentSettingConfiguration};
					if (error.response.status) {
						const errorCode = error.response.data.ErrorCode[0];
						if (daasErrorCodes.includes(errorCode)) {
							error.payload = {
								parentName: action.parentName
							};
						}
					}
					// when not showing modal, we want to send the error back to the calling application to handle it appropriately
					if (!isModalOpen && !isObjectNotFoundCase) {
						const emit = useMessageHubEmit(messageHub.kronosError);
						emit(messageType.error, {error: error, action: action});
					}
					next({type: API_ERRORS, error, action});
					return reject(error);
				}
				// console.log(
				// 	`[${__NAME}] - action.type:`,
				// 	action.type,
				// 	' In order to verify if its a legit discconect, we call [GET_ONLINE_STATE] when api call recieves an error'
				// );
				store.dispatch({type: GET_ONLINE_STATE});
				error.requestUrl = action.meta.api.url;
				action.error = error;
				action.response = {};
				action.responseObj = {};

				if (action && action.payload && action.payload.supressErrorOnUi) {
					return reject(error);
				}

				// when not showing modal, we want to send the error back to the calling application to handle it appropriately
				if (!isModalOpen && !isObjectNotFoundCase) {
					const emit = useMessageHubEmit(messageHub.kronosError);
					emit(messageType.error, {error: {...error, customErrorMessage: action.customErrorMessage || null}});
				}

				next({type: API_ERRORS, error, action});
				return reject(error);
			});
	});

	return !_.isEmpty(trackWithArea) && _.isString(trackWithArea) ? trackPromise(promise, trackWithArea) : promise;
};

export default apiMiddleware;
